import React from 'react';
import './ProfileView.less';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import {
  LocalizePropType,
  isKYCApp,
  UserDataOutput,
  getAge,
  BrandData,
} from 'getaway-data-layer';
import ToggleTab from '../../../components/ToggleTab/ToggleTab';
import AccountTabContainer from './AccountTab/AccountTabContainer';
import ServicesTabContainer from './ServicesTab/ServicesTabContainer';
import { PROFILE_TABS } from '../../../../enums/Profile';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import OpenTransactionsContainer from '../../../components/OpenTransactions/OpenTransactionsContainer';

type Props = {
  localize: LocalizePropType;
  user: UserDataOutput;
  migratedBubUser: UserDataOutput | null;
  isDeUbqShutdown: boolean;
  formattedUbqShutdownDate: string;
  onShutdownCTA: () => void;
  openTransactions: any;
  onClose: () => void;
  activeTab: string;
  setActiveTab: (arg: string) => void;
  enableServicesTab: boolean;
  onDuplicateLogin: () => void;
  brandSettings: BrandData;
  onClickBubDialogButton: () => void;
};

const ProfileView = ({
  localize,
  user,
  migratedBubUser,
  isDeUbqShutdown,
  formattedUbqShutdownDate,
  onShutdownCTA,
  openTransactions,
  onClose,
  activeTab,
  setActiveTab,
  enableServicesTab,
  onDuplicateLogin,
  brandSettings,
  onClickBubDialogButton,
}: Props) => {
  const blockedButOriginalAccountActive =
    user?.blockData?.reason === 'd' &&
    user?.blockData?.originalEmail &&
    !user?.blockData?.originalReason;
  const userAge = getAge(user.dateOfBirth);
  const minimalUserAge = brandSettings?.kycSettings?.minimalUserAge;
  const isUnderAge = userAge && minimalUserAge && userAge < minimalUserAge;

  const blockedDialog = !migratedBubUser &&
    (isUnderAge ||
      ['f', 'd'].includes(user?.blockData?.reason) ||
      ['cC', 'pDr', 'pDe']?.includes(user?.blockData?.details)) && (
      <div className="profile-account-warning">
        <div className="profile-account-warning-title">
          {localize(
            isUnderAge
              ? 'profile.under.age.title'
              : blockedButOriginalAccountActive
              ? 'profile.blocked.title.originalactive'
              : `profile.blocked.title.${user?.blockData?.reason}`
          )}
        </div>
        <div className="profile-account-warning-msg">
          <Markdown rehypePlugins={[rehypeRaw]}>
            {localize(
              isUnderAge
                ? 'profile.under.age.desc'
                : blockedButOriginalAccountActive
                ? 'profile.blocked.msg.originalactive'
                : ['cC', 'pDr', 'pDe'].includes(user?.blockData?.details)
                ? `profile.blocked.msg.${user?.blockData?.details}`
                : `profile.blocked.msg.${user?.blockData?.reason}`
            )}
          </Markdown>
        </div>

        {blockedButOriginalAccountActive && (
          <div className="profile-account-warning-row">
            <button
              className="profile-account-warning-btn"
              onClick={onDuplicateLogin}
            >
              {localize('profile.blocked.button.login')}
            </button>
            {user?.blockData?.originalEmail}
          </div>
        )}
      </div>
    );

  return (
    <div className="screen-container">
      <NavigationBar
        TitleTestId="ProfileView-title"
        DescriptionTestId="ProfileView-description"
        title={localize('dashboard.profile')}
        description=""
        showHelp
        onClose={isKYCApp() ? undefined : onClose}
      />
      <div
        className="screen-body"
        style={enableServicesTab ? { paddingTop: 24 } : {}}
      >
        {isDeUbqShutdown && (
          <div
            className="profile-account-warning"
            data-testid="ProfileView-migratedDialog"
          >
            <div className="profile-account-warning-title">
              {localize('ubq.profile.shutdown.title')}
            </div>
            <div className="profile-account-warning-msg">
              {localize(
                'ubq.profile.shutdown.description',
                formattedUbqShutdownDate
              )}
            </div>

            <div className="profile-account-warning-row">
              <button
                className="profile-account-warning-btn"
                onClick={onShutdownCTA}
              >
                {localize('ubq.profile.shutdown.button')}
              </button>
            </div>
          </div>
        )}
        {migratedBubUser && (
          <div
            className="profile-account-warning"
            data-testid="ProfileView-migratedDialog"
          >
            <div className="profile-account-warning-title">
              {localize('bub.profile.dialog.title')}
            </div>
            <div className="profile-account-warning-msg">
              {localize('bub.profile.dialog.description')}
            </div>

            <div className="profile-account-warning-row">
              <button
                className="profile-account-warning-btn"
                onClick={onClickBubDialogButton}
              >
                {localize('bub.profile.dialog.button')}
              </button>
            </div>
          </div>
        )}
        {blockedDialog}
        {openTransactions && <OpenTransactionsContainer />}
        {enableServicesTab && (
          <ToggleTab
            localize={localize}
            optionOne={localize(`profile.tab.services`)}
            optionTwo={localize(`profile.tab.account`)}
            isOptionOneSelected={activeTab !== PROFILE_TABS.ACCOUNT}
            onToggleValueChange={() => {
              if (activeTab === PROFILE_TABS.ACCOUNT) {
                setActiveTab(PROFILE_TABS.SERVICES);
              } else {
                setActiveTab(PROFILE_TABS.ACCOUNT);
              }
            }}
            hideOR
          />
        )}
        {activeTab === PROFILE_TABS.ACCOUNT && (
          <AccountTabContainer
            migratedBubUser={migratedBubUser}
            isDeUbqShutdown={isDeUbqShutdown}
          />
        )}
        {activeTab === PROFILE_TABS.SERVICES && <ServicesTabContainer />}
      </div>
    </div>
  );
};

export default ProfileView;
